import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";

import appCss from "../styles.css?url";
import { Header } from "@/components/site/Header";
import { VipHeader } from "@/components/site/VipHeader";
import { VipInvitationResume } from "@/components/site/VipInvitationResume";
import { AnunciosFiltersProvider } from "@/contexts/anuncios-filters";
import { AgenciasFiltersProvider } from "@/contexts/agencias-filters";
import { DataScopeProvider } from "@/contexts/DataScopeContext";
import { Toaster } from "sonner";
import { identityNode, websiteNode, safeJsonLd } from "@/lib/jsonld";
import { captureUtmsFromUrl } from "@/lib/utm";


const Footer = lazy(() =>
  import("@/components/site/Footer").then((m) => ({ default: m.Footer })),
);
const CookieBanner = lazy(() =>
  import("@/components/site/CookieBanner").then((m) => ({ default: m.CookieBanner })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscas no existe o ha sido movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página no ha cargado
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo ha fallado por nuestro lado. Puedes reintentar o volver al inicio.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Reintentar
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#00c389" },
      { name: "author", content: "Bizaclia" },
      { title: "Bizaclia" },
      { property: "og:site_name", content: "Bizaclia" },
      { property: "og:locale", content: "es_ES" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@bizaclia" },
      {
        name: "google-site-verification",
        content: "google-site-verification=Uli71hwpg9p0woQH4Q_hqW7D6ByO6AhH54uGDmW46tM",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
    scripts: [
      { src: "https://api.blackbase.io/functions/v1/connect-js", async: true },
      {
        children: `(function(){if(typeof location!=='undefined'&&(location.pathname.indexOf('/admin')===0||location.pathname.indexOf('/agencia-screenshot')===0))return;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-VNTK3N23C9';document.head.appendChild(s);window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','G-VNTK3N23C9');})();`,
      },
      {
        children: `(function(){if(typeof location!=='undefined'&&(location.pathname.indexOf('/admin')===0||location.pathname.indexOf('/agencia-screenshot')===0))return;!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','1548691952657178');fbq('track','PageView');})();`,
      },

      {
        children: `(function(){if(typeof location==='undefined')return;if(location.pathname.indexOf('/conector')!==0)return;(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","xo104s63ey");})();`,
      },
      {
        type: "application/ld+json",
        children: safeJsonLd({
          "@context": "https://schema.org",
          "@graph": [identityNode(), websiteNode("es")],
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const isAdminRoute = useRouterState({
    select: (state) => state.location.pathname.startsWith("/admin"),
  });
  const isVipRoute = useRouterState({
    select: (state) =>
      state.location.pathname === "/vip" ||
      state.location.pathname.startsWith("/portal-vip"),
  });
  const isConectorRoot = useRouterState({
    select: (state) => state.location.pathname === "/conector",
  });
  const isConectorRoute = isConectorRoot;
  const isAgenciaScreenshotRoute = useRouterState({
    select: (state) => state.location.pathname.startsWith("/agencia-screenshot"),
  });

  // Capturar UTMs (first-touch) en el primer render en el cliente.
  useEffect(() => {
    captureUtmsFromUrl();
  }, []);

  // Diferir CookieBanner hasta que el navegador esté inactivo (no es above-the-fold)
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    if (isAdminRoute || isAgenciaScreenshotRoute) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void) => number;
    };
    const idle =
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(() => setShowCookieBanner(true))
        : window.setTimeout(() => setShowCookieBanner(true), 1500);
    return () => {
      if (typeof w.requestIdleCallback === "function") {
        (window as unknown as { cancelIdleCallback?: (id: number) => void })
          .cancelIdleCallback?.(idle as number);
      } else {
        window.clearTimeout(idle as number);
      }
    };
  }, [isAdminRoute, isAgenciaScreenshotRoute]);

    return (
    <QueryClientProvider client={queryClient}>
      <AnunciosFiltersProvider>
       <AgenciasFiltersProvider>
        <DataScopeProvider>
          <div className="flex min-h-screen flex-col">
            {!isConectorRoute && (isVipRoute ? <VipHeader /> : <Header />)}
            <div className="flex-1">
              <Outlet />
            </div>
            {!isAdminRoute && !isConectorRoute && !isAgenciaScreenshotRoute && (
              <Suspense fallback={null}>
                <Footer />
              </Suspense>
            )}
          </div>
          <Toaster position="top-center" richColors closeButton />
          <VipInvitationResume />
          {!isAdminRoute && !isAgenciaScreenshotRoute && showCookieBanner && (
            <Suspense fallback={null}>
              <CookieBanner />
            </Suspense>
          )}
        </DataScopeProvider>
       </AgenciasFiltersProvider>
      </AnunciosFiltersProvider>
    </QueryClientProvider>
  );
}
